import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loadReports, getCustomers, getSites, removeReport } from "../lib/sync";
import { getForm, FORM_TYPE_LIST } from "../lib/formTemplates";
import { useAuth } from "../context/AuthContext";
import MapLink from "../components/MapLink";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Search, Copy, Eye, FileText, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Reports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const duplicateMode = params.get("duplicate") === "1";
  const [reports, setReports] = useState([]);
  const [customers, setCustomers] = useState({});
  const [sites, setSites] = useState({});
  const [q, setQ] = useState("");
  const [tab, setTab] = useState(params.get("status") || "all");
  const [typeFilter, setTypeFilter] = useState(params.get("type") || "all");
  const [delReport, setDelReport] = useState(null);

  const reload = () => loadReports().then(setReports).catch(() => {});

  useEffect(() => {
    reload();
    getCustomers()
      .then((cs) => setCustomers(Object.fromEntries(cs.map((c) => [c.id, c]))))
      .catch(() => {});
    getSites()
      .then((ss) => setSites(Object.fromEntries(ss.map((s) => [s.id, s]))))
      .catch(() => {});
  }, []);

  const doDeleteReport = async () => {
    const r = delReport;
    setDelReport(null);
    try {
      await removeReport(r.id);
      toast.success("Report deleted");
      reload();
    } catch (e) {
      toast.error("Could not delete report");
    }
  };

  const filtered = reports.filter((r) => {
    if (tab !== "all" && r.status !== tab) return false;
    const rType = r.form_type || "service_report";
    if (typeFilter !== "all" && rType !== typeFilter) return false;
    const name = customers[r.customer_id]?.name || "";
    return [name, r.engineer_name, r.service_date, r.final_status]
      .join(" ")
      .toLowerCase()
      .includes(q.toLowerCase());
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">
          {duplicateMode ? "Duplicate a report" : "Reports"}
        </h1>
        <Button className="h-11" onClick={() => navigate("/report/choose")} data-testid="new-report-btn">
          <FileText className="mr-1.5 h-4 w-4" /> New
        </Button>
      </div>

      {duplicateMode && (
        <p className="rounded-lg bg-primary/10 p-3 text-sm text-primary" data-testid="duplicate-hint">
          Pick a previous report to duplicate. Customer, site, heat pump and standard
          details will be pre-filled — set a new service date and edit anything before saving.
        </p>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports…" className="h-12 pl-10" data-testid="reports-search" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
          <TabsTrigger value="draft" data-testid="tab-draft">Drafts</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* report type filter */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="type-filter">
        {[
          { key: "all", label: "All types" },
          { key: "service_report", label: "Service Reports" },
          { key: "commissioning_report", label: "Commissioning Reports" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTypeFilter(t.key)}
            data-testid={`type-${t.key}`}
            className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              typeFilter === t.key
                ? "border-primary bg-primary text-white"
                : "border-border bg-white text-slate-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-2.5">
        {filtered.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`report-row-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900">
                  {customers[r.customer_id]?.name || "Customer"}
                </div>
                <div className="text-xs text-slate-500">
                  {r.service_date || "No date"} • {r.engineer_name}
                </div>
                {(sites[r.site_id]?.address || customers[r.customer_id]?.billing_address) && (
                  <MapLink
                    address={sites[r.site_id]?.address || customers[r.customer_id]?.billing_address}
                    testid={`map-report-${r.id}`}
                    className="mt-0.5 text-xs"
                  />
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={r.status === "completed" ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"} variant="secondary">
                  {r.status}
                </Badge>
                <Badge variant="outline" className="text-[10px]" data-testid={`row-type-${r.id}`}>
                  {getForm(r.form_type).shortLabel}
                </Badge>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              {duplicateMode ? (
                <Button size="sm" className="flex-1" onClick={() => navigate(`/report/new?duplicate=${r.id}`)} data-testid={`duplicate-${r.id}`}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Duplicate this
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(r.status === "completed" ? `/report/${r.id}/view` : `/report/${r.id}/edit`)} data-testid={`open-${r.id}`}>
                    <Eye className="mr-1 h-3.5 w-3.5" /> {r.status === "completed" ? "View" : "Continue"}
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/report/new?duplicate=${r.id}`)} data-testid={`dup-${r.id}`}>
                    <Copy className="mr-1 h-3.5 w-3.5" /> Duplicate
                  </Button>
                  {user?.role === "admin" && (
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDelReport(r)} data-testid={`delete-report-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No reports found.</p>}
      </div>

      <AlertDialog open={!!delReport} onOpenChange={(o) => !o && setDelReport(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this report?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the{" "}
              {customers[delReport?.customer_id]?.name || "customer"} report
              {delReport?.service_date ? ` from ${delReport.service_date}` : ""}. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteReport} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-report">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
